import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { nanoid } from "nanoid";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  CommunicationIntegration,
  MetaWhatsappIntegrationConfig,
} from "@/types/communication-integration";
import {
  createWhatsappIntegrationSchema,
  WhatsappIntegrationFormData,
} from "../utils/integration-schemas";
import { buildWhatsappIntegrationPayload } from "../utils/payload-builders";
import { useUpsertWhatsappIntegration } from "../hooks/communication-integrations.hooks";
import { SecretField } from "./SecretField";

interface WhatsappIntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hotelId: string;
  existing: CommunicationIntegration | null;
}

export const WhatsappIntegrationDialog: React.FC<
  WhatsappIntegrationDialogProps
> = ({ open, onOpenChange, hotelId, existing }) => {
  const { t } = useTranslation();
  const whatsappConfig =
    existing?.config.kind === "META_WHATSAPP" ? existing.config : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-4 overflow-hidden sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {existing
              ? t("communicationChannels.whatsapp.editTitle")
              : t("communicationChannels.whatsapp.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("communicationChannels.whatsapp.dialogDescription")}
          </DialogDescription>
        </DialogHeader>
        {open && (
          <WhatsappIntegrationForm
            hotelId={hotelId}
            existing={existing}
            existingWhatsappConfig={whatsappConfig}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

interface WhatsappIntegrationFormProps {
  hotelId: string;
  existing: CommunicationIntegration | null;
  existingWhatsappConfig: MetaWhatsappIntegrationConfig | null;
  onClose: () => void;
}

const WhatsappIntegrationForm: React.FC<WhatsappIntegrationFormProps> = ({
  hotelId,
  existing,
  existingWhatsappConfig,
  onClose,
}) => {
  const { t } = useTranslation();
  const isFirstSetup = existing === null;
  const isCurrentlyEnabled = existing?.enabled ?? false;
  const canClearSecrets = !!existing && !isCurrentlyEnabled;
  const { mutate: upsert, isPending } = useUpsertWhatsappIntegration(hotelId);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<WhatsappIntegrationFormData>({
    resolver: zodResolver(
      createWhatsappIntegrationSchema({ isFirstSetup, isCurrentlyEnabled }),
    ),
    defaultValues: {
      enabled: isCurrentlyEnabled,
      accessToken: "",
      clearAccessToken: false,
      phoneNumberId: existingWhatsappConfig?.phoneNumberId ?? "",
      wabaId: existingWhatsappConfig?.wabaId ?? "",
      displayPhoneNumber: existingWhatsappConfig?.displayPhoneNumber ?? "",
      appSecret: "",
      clearAppSecret: false,
      webhookVerifyToken: "",
      clearWebhookVerifyToken: false,
      graphApiVersion: existingWhatsappConfig?.graphApiVersion ?? "v22.0",
      phoneRegistrationPin: "",
    },
  });

  const onSubmit = (data: WhatsappIntegrationFormData) => {
    upsert(buildWhatsappIntegrationPayload(data), {
      onSuccess: (result) => {
        if (data.enabled && result.healthStatus !== "HEALTHY") {
          toast.warning(
            t("communicationChannels.whatsapp.saveSetupAttention"),
            {
              description: result.lastError ?? undefined,
            },
          );
        } else {
          toast.success(t("communicationChannels.whatsapp.saveSuccess"));
        }
        onClose();
      },
      onError: () => {
        toast.error(t("communicationChannels.whatsapp.saveError"));
      },
    });
  };

  const enabled = watch("enabled");

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex min-h-0 flex-1 flex-col gap-4"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
        <div className="flex items-center justify-between rounded-md border p-3">
          <div className="flex flex-col">
            <Label>{t("communicationChannels.whatsapp.enabledLabel")}</Label>
            <span className="text-muted-foreground text-xs">
              {t("communicationChannels.whatsapp.enabledHelp")}
            </span>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(value) => setValue("enabled", value)}
            aria-label={t("communicationChannels.whatsapp.enabledLabel")}
          />
        </div>

        <SecretField
          id="wa-access-token"
          label={t("communicationChannels.whatsapp.fields.accessToken")}
          value={watch("accessToken")}
          cleared={watch("clearAccessToken")}
          isFirstSetup={isFirstSetup}
          canClear={canClearSecrets}
          error={errors.accessToken?.message}
          onChange={(value) =>
            setValue("accessToken", value, { shouldValidate: true })
          }
          onToggleClear={(next) =>
            setValue("clearAccessToken", next, { shouldValidate: true })
          }
        />

        <TextField
          id="wa-phone-number-id"
          label={t("communicationChannels.whatsapp.fields.phoneNumberId")}
          placeholder="123456789012345"
          error={errors.phoneNumberId?.message}
          required
          {...register("phoneNumberId")}
        />
        <TextField
          id="wa-waba-id"
          label={t("communicationChannels.whatsapp.fields.wabaId")}
          placeholder="987654321098765"
          error={errors.wabaId?.message}
          required
          {...register("wabaId")}
        />
        <TextField
          id="wa-display-phone"
          label={t("communicationChannels.whatsapp.fields.displayPhoneNumber")}
          placeholder="+15551234567"
          error={errors.displayPhoneNumber?.message}
          required
          {...register("displayPhoneNumber")}
        />

        <SecretField
          id="wa-app-secret"
          label={t("communicationChannels.whatsapp.fields.appSecret")}
          value={watch("appSecret")}
          cleared={watch("clearAppSecret")}
          isFirstSetup={isFirstSetup}
          canClear={canClearSecrets}
          error={errors.appSecret?.message}
          onChange={(value) =>
            setValue("appSecret", value, { shouldValidate: true })
          }
          onToggleClear={(next) =>
            setValue("clearAppSecret", next, { shouldValidate: true })
          }
        />

        <div className="flex flex-col gap-2">
          <SecretField
            id="wa-webhook-verify-token"
            label={t(
              "communicationChannels.whatsapp.fields.webhookVerifyToken",
            )}
            value={watch("webhookVerifyToken")}
            cleared={watch("clearWebhookVerifyToken")}
            isFirstSetup={isFirstSetup}
            canClear={canClearSecrets}
            error={errors.webhookVerifyToken?.message}
            onChange={(value) =>
              setValue("webhookVerifyToken", value, { shouldValidate: true })
            }
            onToggleClear={(next) =>
              setValue("clearWebhookVerifyToken", next, {
                shouldValidate: true,
              })
            }
          />
          <Button
            type="button"
            variant="new_outline"
            size="sm"
            className="w-fit"
            disabled={watch("clearWebhookVerifyToken")}
            onClick={() => {
              setValue("clearWebhookVerifyToken", false);
              setValue("webhookVerifyToken", nanoid(32), {
                shouldValidate: true,
              });
            }}
          >
            <Sparkles className="size-4" />
            {t("communicationChannels.whatsapp.generateVerifyToken")}
          </Button>
        </div>

        <TextField
          id="wa-graph-version"
          label={t("communicationChannels.whatsapp.fields.graphApiVersion")}
          placeholder="v22.0"
          error={errors.graphApiVersion?.message}
          required
          {...register("graphApiVersion")}
        />

        <TextField
          id="wa-registration-pin"
          label={t(
            "communicationChannels.whatsapp.fields.phoneRegistrationPin",
          )}
          placeholder="123456"
          error={errors.phoneRegistrationPin?.message}
          inputMode="numeric"
          maxLength={6}
          {...register("phoneRegistrationPin")}
        />

        <ProvisioningPanel enabled={enabled} />
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="new_outline"
          onClick={onClose}
          disabled={isPending}
        >
          {t("communicationChannels.actions.cancel")}
        </Button>
        <Button type="submit" variant="new_default" disabled={isPending}>
          {isPending
            ? t("communicationChannels.actions.saving")
            : t("communicationChannels.actions.save")}
        </Button>
      </DialogFooter>
    </form>
  );
};

const PROVISIONING_STEP_KEYS = [
  "communicationChannels.whatsapp.provisioning.steps.1",
  "communicationChannels.whatsapp.provisioning.steps.2",
  "communicationChannels.whatsapp.provisioning.steps.3",
  "communicationChannels.whatsapp.provisioning.steps.4",
  "communicationChannels.whatsapp.provisioning.steps.5",
] as const;

const ProvisioningPanel: React.FC<{ enabled: boolean }> = ({ enabled }) => {
  const { t } = useTranslation();

  return (
    <section className="bg-muted/40 mt-2 flex flex-col gap-3 rounded-md border p-3">
      <div className="flex flex-col gap-1">
        <h4 className="text-foreground text-sm font-medium">
          {t("communicationChannels.whatsapp.provisioning.title")}
        </h4>
        <p className="text-muted-foreground text-xs">
          {enabled
            ? t(
                "communicationChannels.whatsapp.provisioning.descriptionEnabled",
              )
            : t(
                "communicationChannels.whatsapp.provisioning.descriptionDisabled",
              )}
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-foreground text-xs font-medium">
          {t("communicationChannels.whatsapp.provisioning.stepsTitle")}
        </span>
        <ol className="text-muted-foreground list-decimal space-y-1 pl-4 text-xs">
          {PROVISIONING_STEP_KEYS.map((stepKey) => (
            <li key={stepKey}>{t(stepKey)}</li>
          ))}
        </ol>
      </div>
    </section>
  );
};

interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  error?: string;
}

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({ id, label, error, required, ...props }, ref) => (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>
        {label}
        {required && " *"}
      </Label>
      <Input id={id} ref={ref} autoComplete="off" {...props} />
      {error && <p className="text-destructive text-xs">{error}</p>}
    </div>
  ),
);
TextField.displayName = "TextField";
